import React from "react"

import { useActiveJob } from "@/contexts/ActiveJobContext"
import { useApplication } from "@/contexts/ApplicationContext"
import { useApplicationStage } from "@/hooks/useApplicationStage"

import StepperConnector from "./stepper/StepperConnector"
import StepperStep from "./stepper/StepperStep"

const ApplicationStepper: React.FC = () => {
  const { application } = useApplication()
  const { job } = useActiveJob()
  const { currentStage, completedStages } = useApplicationStage(application)

  // Define steps with their contained stages
  const steps = [
    {
      title: "Details",
      stages: ["basic-details"],
      number: 1,
    },
    {
      title: "Assessment",
      stages: ["driver-onroad-test"],
      number: 2,
    },
    {
      title: "Documents",
      stages: ["documents"],
      number: 3,
    },
    {
      title: "Orientation",
      stages: ["screening", "orientation", "orientation-quiz", "personal-details"],
      number: 4,
    },
    {
      title: "Trial",
      stages: ["trial"],
      number: 5,
    },
    {
      title: "Onboarding",
      stages: ["contract", "vehicle-collection"],
      number: 6,
    },
  ]

  // Filter steps based on job stages
  const filteredSteps = steps.filter(
    (step) =>
      step.title === "Onboarding" ||
      step.stages.some((stage) => job?.stages?.some((jobStage) => jobStage.id === stage)),
  )

  // Adjust step numbers to be sequential after filtering
  const allSteps = filteredSteps.map((step, index) => ({
    ...step,
    number: index + 1, // Reassign numbers starting from 1
  }))

  // For debugging
  console.debug("ApplicationStepper: Using filtered steps with adjusted numbers", allSteps)

  const getMobileSteps = () => {
    // Find the step that contains the current stage
    const currentStepIndex = allSteps.findIndex((step) =>
      step.stages.some((stage) => stage === currentStage),
    )

    if (currentStepIndex === -1) return allSteps.slice(0, 4)

    if (currentStepIndex >= 4) {
      const steps = []
      steps.push(allSteps[0])

      if (currentStepIndex > 1) {
        steps.push(allSteps[currentStepIndex - 1])
      }

      steps.push(allSteps[currentStepIndex])

      if (currentStepIndex + 1 < allSteps.length) {
        steps.push(allSteps[currentStepIndex + 1])
      }

      return steps
    }

    const steps = []
    const finalStep = allSteps[allSteps.length - 1]

    steps.push(allSteps[currentStepIndex])

    if (currentStepIndex + 1 < allSteps.length) {
      const nextStep = allSteps[currentStepIndex + 1]
      steps.push(nextStep)

      if (currentStepIndex + 2 < allSteps.length) {
        const nextNextStep = allSteps[currentStepIndex + 2]
        steps.push(nextNextStep)

        if (nextNextStep !== finalStep) {
          steps.push(finalStep)
        }
      } else if (nextStep !== finalStep) {
        steps.push(finalStep)
      }
    }

    return steps
  }

  const mobileSteps = getMobileSteps()

  // Modify the currentStage and completedStages for StepperStep component
  const getStepperProps = (step: (typeof allSteps)[0]) => {
    // For the stage prop, always use the first stage in the step
    const stageToUse = step.stages[0]

    // Pass all stages in this step to allow checking if current stage is any of them
    const allStagesInStep = step.stages

    // Use the true application current stage
    const currentStageToUse = application.currentStage

    // Find the index of this step and the current step in the overall steps array
    const stepIndex = allSteps.findIndex((s) => s.stages.includes(stageToUse))
    const currentStepIndex = allSteps.findIndex((s) =>
      s.stages.some((stage) => stage === currentStageToUse),
    )

    // Mark as completed if this step comes before the current step
    const isCompleted = stepIndex < currentStepIndex
    const completedStagesToUse = isCompleted ? [stageToUse] : []

    return {
      stage: stageToUse,
      allStagesInStep: allStagesInStep, // Pass all stages in this step
      currentStage: currentStageToUse,
      completedStages: completedStagesToUse,
    }
  }

  const shouldUseDottedLine = (index: number) => {
    if (mobileSteps.length < 2 || index >= mobileSteps.length - 1) return false

    const currentStepIndex = allSteps.indexOf(mobileSteps[index])
    const nextStepIndex = allSteps.indexOf(mobileSteps[index + 1])

    return nextStepIndex - currentStepIndex > 1
  }

  console.log("ApplicationStepper Debug:", {
    currentStage,
    completedStages,
    jobStages: job?.stages,
    mobileSteps: mobileSteps.map((s) => ({ stages: s.stages, title: s.title })),
    applicationData: {
      applicationId: application.applicationId,
      documents: application.documents,
      orientation: application.orientation,
      formsCompleted: application.formsCompleted,
      vehicleCollection: application.vehicleCollection,
    },
  })

  return (
    <div className="mb-6 rounded-lg bg-white p-4 shadow-sm md:p-6">
      {/* Desktop version - show all steps */}
      <div className="hidden items-center md:flex">
        {allSteps.map((step, index) => (
          <React.Fragment key={step.title}>
            <StepperStep title={step.title} {...getStepperProps(step)} number={step.number} />
            {index < allSteps.length - 1 && <StepperConnector />}
          </React.Fragment>
        ))}
      </div>

      {/* Mobile version - show current, next, next-next, and final stages */}
      <div className="flex items-center md:hidden">
        {mobileSteps.map((step, index) => (
          <React.Fragment key={step.title}>
            <StepperStep title={step.title} {...getStepperProps(step)} number={step.number} />
            {index < mobileSteps.length - 1 && (
              <div className="mx-2 flex-1">
                <div
                  className={`h-2 border-t-2 ${
                    shouldUseDottedLine(index) ? "border-dotted" : "border-solid"
                  } border-gray-300`}
                ></div>
              </div>
            )}
          </React.Fragment>
        ))}
      </div>
    </div>
  )
}

export default ApplicationStepper
